import { ModalHeader } from '@/design-system/components/modal';
import { TextTitle } from '@/design-system/components/typography';
import { ActivityIndicator, Pressable, SxProp, useDripsyTheme, View } from 'dripsy';
import { uniqBy } from 'lodash';
import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput } from '@/design-system/components/inputs';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { DigitalDepositStackParamList, DigitalDepositStackRoutes } from '@/features/account-management/screens/digital-deposit/type';
import { Blockchain, DigitalAsset } from '@utx/utx-access-client';
import { useDigitalAssets } from '@/features/account-management/screens/details/hooks/useAccount.hook';
import { CurrenciesIcon } from '@/features/earn/components';
import { PressableView } from '@/design-system/components/table/UniTable';

export interface DigitalDepositProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  account: string;
  record?: any;
  refresh?: () => void;
}

export const DigitalDepositWebListScreen: React.FC<DigitalDepositProps> = (props) => {
  const { onClose, onLayout, account, record, refresh } = props;
  const { t } = useTranslation('digital-injection');
  const layout = useContext(LayoutContext);
  const { theme } = useDripsyTheme();
  const { body, textMb } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<DigitalDepositStackParamList>>();

  const [showView, setShowView] = useState(false);
  const onChangeView = () => {
    setShowView(!showView);
  };

  const [search, setSearch] = useState('');
  const { isLoadingDigitalAssets, assets, chains } = useDigitalAssets({});
  const [assetsDispay, setAssetsDispay] = useState<(DigitalAsset & { assetChains: string[] })[]>([]);
  useEffect(() => {
    if (isLoadingDigitalAssets) return;
    let assetsFlag: (DigitalAsset & { assetChains: string[] })[] = [];
    assets.forEach((asset) => {
      let index = assetsFlag.findIndex((af) => af.name == asset.name);
      if (index == -1) {
        assetsFlag.push({ ...asset, assetChains: [asset.chain || ''] });
      } else {
        assetsFlag[index].assetChains.push(asset.chain || '');
      }
    });

    if (!search) setAssetsDispay(assetsFlag);
    let assetsDispayFlag = assetsFlag?.filter((asset: DigitalAsset & { assetChains: string[] }) => {
      return (
        asset?.chain && record?.assets?.includes(asset?.name) && asset?.name?.toLocaleLowerCase()?.includes(search?.toLocaleLowerCase())
      );
    });
    setAssetsDispay(assetsDispayFlag);
  }, [search, isLoadingDigitalAssets]);

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader title={t('digital-injection::glossary.digital-assets')} closeModal={onClose} />
      <View sx={body}>
        <TextTitle sx={textMb} semiBold>
          {t('digital-injection::select-asset.text--please-select-asset')}
        </TextTitle>
        <View sx={{ pb: '$3' }}>
          <UniInput
            prefixIcon="search"
            wrapperSx={{ flex: 1 }}
            sx={{ width: '100%' }}
            onValueChange={(value) => setSearch(value || '')}
            placeholder={t('digital-injection::glossary.search-by-asset')}
          />
        </View>
        <View sx={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          {!isLoadingDigitalAssets ? (
            assetsDispay.length > 0 ? (
              assetsDispay?.map((asset, index) => (
                <PressableView
                  sx={{
                    width: '48%',
                    my: '$1',
                    mr: index % 2 == 0 ? '4%' : undefined
                  }}
                  onPress={() => {
                    navigation.navigate(DigitalDepositStackRoutes.Address, {
                      onLayout,
                      account,
                      refresh,
                      record: { ...asset, chains: chains },
                      onChangeView
                    });
                  }}>
                  {({ hovered }) => (
                    <View
                      sx={{
                        padding: '$3',
                        backgroundColor: '#fafafa',
                        borderRadius: '$radius1',
                        border: `1px solid ${index == 0 || hovered ? '#dac797' : '#fafafa'}`
                      }}>
                      <CurrenciesIcon
                        asset={asset?.name || ''}
                        textSize="textTitle"
                        textSemiBold
                        textSx={{ color: '$textPrimary' }}
                        textBold={false}
                      />
                    </View>
                  )}
                </PressableView>
              ))
            ) : (
              <View sx={{ width: '100%', display: 'flex', alignItems: 'center', paddingTop: 20 }}>
                {t('digital-injection::injection-instruction.text--no-digital-asset-available')}
              </View>
            )
          ) : (
            <View sx={{ padding: '$5' }}>
              <ActivityIndicator size="large" color={(theme.colors?.$disable as string) || '#F0F0F0'} />
            </View>
          )}
        </View>
        {showView && <View sx={{ height: '$2', width: '100%' }} />}
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  }
};
